import React, { useEffect, useState } from 'react';
import { X, Sparkles, MapPin, Map as MapIcon, AlertCircle } from 'lucide-react';
import C from '../../constants/colors';
import * as historyApi from '../../api/history';
import MapPickerModal from './MapPickerModal';

export default function KBPromotionModal({
  snippetText, sourceDocId, sourceChunkId, userContext,
  sites, zones, onClose, onPromoted,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', body: '', siteId: userContext?.siteId || '', zoneId: '',
    workType: '', hazards: [], equipmentRef: '', keywords: [],
    gpsLat: null, gpsLng: null,
  });

  useEffect(() => {
    let cancelled = false;
    historyApi.promoteSuggest({ snippetText, sourceDocId, sourceChunkId, userContext })
      .then(({ suggestion }) => {
        if (cancelled) return;
        setForm(prev => ({
          ...prev,
          title:        suggestion.title || prev.title,
          body:         suggestion.body || prev.body,
          workType:     suggestion.work_type || prev.workType,
          hazards:      suggestion.hazards || prev.hazards,
          equipmentRef: suggestion.equipment_ref || prev.equipmentRef,
          keywords:     suggestion.keywords || prev.keywords,
          gpsLat:       suggestion.gps_lat ?? prev.gpsLat,
          gpsLng:       suggestion.gps_lng ?? prev.gpsLng,
        }));
      })
      .catch(err => setError('AI suggestion failed: ' + (err?.response?.data?.error || err.message)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [snippetText, sourceDocId, sourceChunkId, userContext]);

  async function handleSave() {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body required'); return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await historyApi.promote({
        ...form,
        sourceDocId, sourceChunkId,
        sourceExcerpt: snippetText.slice(0, 1000),
      });
      onPromoted?.(created);
    } catch (err) {
      setError('Save failed: ' + (err?.response?.data?.error || err.message));
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated,
        border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: 600,
        maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Sparkles size={18} color={C.amber} />
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, flex: 1 }}>Promote to Knowledge Base</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: 8,
            padding: '10px 12px', marginBottom: 12, fontSize: 13, color: C.red,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
            Asking AI to extract structured tags…
          </div>
        ) : (
          <>
            <Field label="Title *">
              <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                style={inputStyle()} />
            </Field>
            <Field label="Body / Summary *">
              <textarea rows={4} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                style={inputStyle()} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Site">
                <select value={form.siteId} onChange={e => setForm(p => ({ ...p, siteId: e.target.value }))}
                  style={inputStyle()}>
                  <option value="">— Tenant-wide —</option>
                  {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Zone">
                <select value={form.zoneId} onChange={e => setForm(p => ({ ...p, zoneId: e.target.value }))}
                  style={inputStyle()}>
                  <option value="">— None —</option>
                  {(zones || []).filter(z => !form.siteId || z.siteId === form.siteId)
                    .map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Work type">
                <input type="text" value={form.workType}
                  onChange={e => setForm(p => ({ ...p, workType: e.target.value }))}
                  placeholder="hot_work, confined_space, …"
                  style={inputStyle()} />
              </Field>
              <Field label="Equipment ref">
                <input type="text" value={form.equipmentRef}
                  onChange={e => setForm(p => ({ ...p, equipmentRef: e.target.value }))}
                  placeholder="T-203, Boiler B-1, …"
                  style={inputStyle()} />
              </Field>
            </div>

            <Field label={`Hazards (${form.hazards.length})`}>
              <ChipsInput value={form.hazards} onChange={hazards => setForm(p => ({ ...p, hazards }))}
                placeholder="add hazard, press Enter" />
            </Field>
            <Field label={`Keywords (${form.keywords.length})`}>
              <ChipsInput value={form.keywords} onChange={keywords => setForm(p => ({ ...p, keywords }))}
                placeholder="add keyword, press Enter" />
            </Field>

            <Field label="GPS Location (optional)">
              {form.gpsLat != null && form.gpsLng != null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.surface,
                  border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
                  <MapPin size={14} color={C.green} />
                  <span style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>
                    {form.gpsLat.toFixed(5)}, {form.gpsLng.toFixed(5)}
                  </span>
                  <button type="button" onClick={() => setMapPickerOpen(true)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                      background: 'transparent', color: C.textSecondary, fontSize: 11, cursor: 'pointer' }}>
                    Change
                  </button>
                  <button type="button" onClick={() => setForm(p => ({ ...p, gpsLat: null, gpsLng: null }))}
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.red}44`,
                      background: C.red + '11', color: C.red, fontSize: 11, cursor: 'pointer' }}>
                    Clear
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setMapPickerOpen(true)}
                  disabled={!form.siteId}
                  title={!form.siteId ? 'Pick a site first' : undefined}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px dashed ${form.siteId ? C.blue + '55' : C.border}`,
                    background: form.siteId ? C.blue + '11' : C.surface,
                    color: form.siteId ? C.blue : C.textMuted,
                    fontSize: 13, fontWeight: 600, cursor: form.siteId ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <MapIcon size={14} />
                  Pin on Map
                </button>
              )}
            </Field>

            <Field label="Source">
              <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
                doc {sourceDocId} · chunk {sourceChunkId}
              </div>
            </Field>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} disabled={saving}
                style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${C.border}`,
                         background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                         background: saving ? C.surface : C.blue, color: saving ? C.textMuted : '#fff',
                         fontSize: 13, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
                {saving ? 'Saving…' : 'Add to KB'}
              </button>
            </div>
          </>
        )}
      </div>

      {mapPickerOpen && (
        <MapPickerModal
          site={(sites || []).find(s => s.id === form.siteId) || null}
          zones={(zones || []).filter(z => z.siteId === form.siteId)}
          initialLat={form.gpsLat}
          initialLng={form.gpsLng}
          onPick={({ lat, lng }) => setForm(p => ({ ...p, gpsLat: lat, gpsLng: lng }))}
          onClose={() => setMapPickerOpen(false)}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: C.textMuted, marginBottom: 4,
        fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function inputStyle() {
  return {
    width: '100%', boxSizing: 'border-box', background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px',
    color: C.textPrimary, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
  };
}

function ChipsInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px',
      minHeight: 36 }}>
      {(value || []).map((tag, i) => (
        <span key={i} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4,
          background: C.blue + '22', color: C.blue, display: 'flex', alignItems: 'center', gap: 4 }}>
          {tag}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.blue,
              padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
        </span>
      ))}
      <input type="text" value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && input.trim()) {
            e.preventDefault();
            onChange([...(value || []), input.trim()]);
            setInput('');
          }
        }}
        placeholder={placeholder}
        style={{ flex: 1, minWidth: 100, background: 'transparent', border: 'none',
          color: C.textPrimary, fontSize: 12, outline: 'none', fontFamily: 'inherit' }} />
    </div>
  );
}
